import { Reducer } from 'redux';
import { ValidationProps, ValidationLevel } from 'ui/common/validation/types';
import { codetableOptionsReducer } from '../common';
import { CodeFieldWireKey } from '../types-store';
import {
  ACTION,
  RequestCodeItem, ReceiveCodeItem, FailCodeItem, CopyDescriptionToAllTranslations,
  RequestSaveCodetableItem, ReceiveSaveCodetableItem, FailSaveCodetableItem,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  UpdateListNode, UpdateParent, UpdateDisabled, UpdateDescription, UpdateLongDescription, UpdateSequence,
  UpdateUserGroups, UpdateExcludedFields, UpdateCaseTypes, UpdateCodeCategory,
  RequestCreateCodeItem, ReceiveCreateCodeItem, FailCreateCodeItem,
  UpdateValidation, ResetValidation, ResetDescriptionValidation, ResetLongDescriptionValidation,
  ClearValidation, CopyLongDescriptionToAllTranslations, SwitchTranslationsToggle,
  ClearCodetableOptions, ResetAllDescriptionsValidation, ResetAllLongDescriptionsValidation,
  UpdateCodetableTypes, UpdateIcon, UpdateColour, UpdateColourCode, UpdateByKey, UpdateOriginalCodeByKey,
  RequestParentCategory, ReceiveParentCategory, FailParentCategory, UpdateMeasurementSystem, UpdateUomConversion,
  UpdateUomConversionFactorItem, RequestCheckIsUomExcluded, ReceiveCheckIsUomExcluded, FailCheckIsUomExcluded,
  RequestUomConversionFactors, ReceiveUomConversionFactors, FailUomConversionFactors, ClearUomConversionFactors,
  ClearUomExcluded, CreateInitialUomConversionFactors
} from './action-types';
import { clearValidationForNewItems } from './codetable-item-helpers';
import { CodetableItemState } from './types-store';

export const INITIAL_CODETABLE_ITEM_STATE: CodetableItemState = {
  fetchingCodetableId: undefined,
  fetchingCodeId: undefined,
  fetchingCodeAccessCodes: false,
  fetchingCreateOptions: false,
  fetchingIsUomExcludedCheck: false,
  deletingCodeItem: false,
  savingCodetableItem: false,
  originalCodeItem: undefined,
  codeItem: undefined,
  codetableOptions: {},
  validation: {},
  hasTranslationsToggled: undefined,
  isUomExcluded: undefined,
  uomConversionFactors: undefined,
  initialUomConversionFactors: undefined,
  fetchingUomConversionFactors: false
};

const itemReducers: { [indexer: string]: Reducer<CodetableItemState> } = {

  ...codetableOptionsReducer as { [indexer: string]: Reducer<CodetableItemState> },

  [ACTION.REQUEST_CODE_ITEM]: (state: CodetableItemState, action: RequestCodeItem): CodetableItemState => {

    return {
      ...state,
      fetchingCodetableId: action.codetableId,
      fetchingCodeId: action.codeId
    };
  },

  [ACTION.RECEIVE_CODE_ITEM]: (state: CodetableItemState, action: ReceiveCodeItem): CodetableItemState => {

    const { payload: { codeId, codeItem, codetableId } } = action;

    return codetableId === state.fetchingCodetableId && codeId === state.fetchingCodeId
      ? {
        ...state,
        fetchingCodetableId: undefined,
        fetchingCodeId: undefined,
        codeItem,
        originalCodeItem: codeItem
      }
      : state;
  },

  [ACTION.FAIL_CODE_ITEM]: (state: CodetableItemState, action: FailCodeItem): CodetableItemState => {

    return action.codetableId === state.fetchingCodetableId && action.codeId === state.fetchingCodeId
      ? {
        ...state,
        fetchingCodetableId: undefined,
        fetchingCodeId: undefined
      }
      : state;
  },

  [ACTION.REQUEST_CREATE_CODE_ITEM]: (state: CodetableItemState, action: RequestCreateCodeItem): CodetableItemState => {

    return {
      ...state,
      fetchingCreateOptions: true
    };
  },

  [ACTION.RECEIVE_CREATE_CODE_ITEM]: (state: CodetableItemState, action: ReceiveCreateCodeItem): CodetableItemState => {

    return {
      ...state,
      fetchingCreateOptions: false,
      codeItem: action.codeItem,
      originalCodeItem: action.codeItem,
      isUomExcluded: undefined
    };
  },

  [ACTION.FAIL_CREATE_CODE_ITEM]: (state: CodetableItemState, action: FailCreateCodeItem): CodetableItemState => {

    return {
      ...state,
      fetchingCreateOptions: false
    };
  },

  [ACTION.REQUEST_CODE_ITEM_DELETE]: (state: CodetableItemState, action: RequestCodeItemDelete): CodetableItemState => {
    return {
      ...state,
      deletingCodeItem: true
    };
  },

  [ACTION.RECEIVE_CODE_ITEM_DELETE]: (state: CodetableItemState, action: ReceiveCodeItemDelete): CodetableItemState => {
    return {
      ...state,
      deletingCodeItem: false
    };
  },

  [ACTION.FAIL_CODE_ITEM_DELETE]: (state: CodetableItemState, action: FailCodeItemDelete): CodetableItemState => {
    return {
      ...state,
      deletingCodeItem: false
    };
  },

  [ACTION.UPDATE_LIST_NODE]: (state: CodetableItemState, action: UpdateListNode): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        node: action.node
      }
    };
  },

  [ACTION.UPDATE_PARENT]: (state: CodetableItemState, action: UpdateParent): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        parent: action.parent
      }
    };
  },

  [ACTION.UPDATE_DISABLED]: (state: CodetableItemState, action: UpdateDisabled): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        disabled: action.disabled
      }
    };
  },

  [ACTION.UPDATE_USER_GROUPS]: (state: CodetableItemState, action: UpdateUserGroups): CodetableItemState => {

    const validation = clearValidationForNewItems({
      existingItems: state.codeItem?.userGroups,
      newItems: action.userGroups,
      validation: state.validation,
      validationKey: CodeFieldWireKey.subset
    });

    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        userGroups: action.userGroups
      },
      validation
    };
  },

  [ACTION.UPDATE_EXCLUDED_FIELDS]: (state: CodetableItemState, action: UpdateExcludedFields): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        excludeFields: action.excludedFields
      }
    };
  },

  [ACTION.UPDATE_CASE_TYPES]: (state: CodetableItemState, action: UpdateCaseTypes): CodetableItemState => {

    const validation = clearValidationForNewItems({
      existingItems: state.codeItem?.caseTypes,
      newItems: action.caseTypes,
      validation: state.validation,
      validationKey: CodeFieldWireKey.application
    });

    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        caseTypes: action.caseTypes
      },
      validation
    };
  },

  [ACTION.UPDATE_CODETABLE_TYPES]: (state: CodetableItemState, action: UpdateCodetableTypes): CodetableItemState => {

    const validation = clearValidationForNewItems({
      existingItems: state.codeItem?.codetableTypes,
      newItems: action.codetableTypes,
      validation: state.validation,
      validationKey: CodeFieldWireKey.class
    });

    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        codetableTypes: action.codetableTypes
      },
      validation
    };
  },

  [ACTION.UPDATE_SEQUENCE]: (state: CodetableItemState, action: UpdateSequence): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        sequence: action.sequence
      }
    };
  },

  [ACTION.UPDATE_ICON]: (state: CodetableItemState, action: UpdateIcon): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        icon: action.icon
      }
    };
  },

  [ACTION.UPDATE_COLOUR]: (state: CodetableItemState, action: UpdateColour): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        colour: action.colour
      }
    };
  },

  [ACTION.UPDATE_COLOUR_CODE]: (state: CodetableItemState, action: UpdateColourCode): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        colourCode: action.colourCode
      }
    };
  },

  [ACTION.UPDATE_MEASUREMENT_SYSTEM]: (state: CodetableItemState, action: UpdateMeasurementSystem): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        measurementSystem: action.measurementSystem
      }
    };
  },

  [ACTION.UPDATE_UOM_CONVERSION]: (state: CodetableItemState, action: UpdateUomConversion): CodetableItemState => {
    return {
      ...state,
      uomConversionFactors: action.uomConversion
    };
  },

  [ACTION.UPDATE_UOM_CONVERSION_FACTOR_ITEM]: (state: CodetableItemState, action: UpdateUomConversionFactorItem): CodetableItemState => {

    const uomConversionFactors = state.uomConversionFactors
      .map(item => item.value === action.id
        ? { ...item, factor: action.value }
        : item);

    return {
      ...state,
      uomConversionFactors,
      codeItem: {
        ...state.codeItem,
        uomConversion: uomConversionFactors
      }
    };
  },

  [ACTION.UPDATE_CODE_CATEGORY]: (state: CodetableItemState, action: UpdateCodeCategory): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        category: action.codeCategory
      }
    };
  },

  [ACTION.REQUEST_SAVE_CODETABLE_ITEM]: (state: CodetableItemState, action: RequestSaveCodetableItem): CodetableItemState => {
    return {
      ...state,
      savingCodetableItem: true
    };
  },

  [ACTION.RECEIVE_SAVE_CODETABLE_ITEM]: (state: CodetableItemState, action: ReceiveSaveCodetableItem): CodetableItemState => {
    return {
      ...state,
      savingCodetableItem: false,
      originalCodeItem: state.codeItem
    };
  },

  [ACTION.FAIL_SAVE_CODETABLE_ITEM]: (state: CodetableItemState, action: FailSaveCodetableItem): CodetableItemState => {
    return {
      ...state,
      savingCodetableItem: false
    };
  },

  [ACTION.COPY_DESCRIPTION_TO_ALL_TRANSLATIONS]: (
    state: CodetableItemState,
    action: CopyDescriptionToAllTranslations
  ): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        ...(!!action.description && { description: action.description })
      }
    };
  },

  [ACTION.COPY_LONG_DESCRIPTION_TO_ALL_TRANSLATIONS]: (
    state: CodetableItemState,
    action: CopyLongDescriptionToAllTranslations
  ): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        ...(!!action.longDescription && { longDescription: action.longDescription })
      }
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: CodetableItemState, action: UpdateValidation): CodetableItemState => {
    return {
      ...state,
      validation: action.validation
    };
  },

  [ACTION.RESET_VALIDATION]: (state: CodetableItemState, action: ResetValidation): CodetableItemState => {
    const validation = { ...state.validation, [action.key]: undefined };

    return {
      ...state,
      validation
    };
  },

  [ACTION.CLEAR_VALIDATION]: (state: CodetableItemState, action: ClearValidation): CodetableItemState => {
    return {
      ...state,
      validation: {}
    };
  },

  [ACTION.RESET_ALL_DESCRIPTIONS_VALIDATION]: (state: CodetableItemState, action: ResetAllDescriptionsValidation): CodetableItemState => {

    const source: ValidationProps = state.validation.language;

    let validation: { [key: string]: ValidationProps } = undefined;

    if (!!source && !!source.messages && source.messages.length > 0) {
      const messages = source.messages.filter(message => !!message.parameters.description);
      const level = messages.some(message => message.level === ValidationLevel.error) ? ValidationLevel.error : ValidationLevel.success;

      validation = level === ValidationLevel.success
        ? { ...state.validation, language: undefined }
        : { ...state.validation, language: { level, messages } };
    } else {
      validation = { ...state.validation, language: undefined };
    }

    return {
      ...state,
      validation
    };
  },

  [ACTION.RESET_ALL_DESCRIPTIONS_VALIDATION]: (state: CodetableItemState,
    action: ResetAllLongDescriptionsValidation): CodetableItemState => {

    const source: ValidationProps = state.validation.language;

    let validation: { [key: string]: ValidationProps } = undefined;

    if (!!source && !!source.messages && source.messages.length > 0) {
      const messages = source.messages.filter(message => !!message.parameters.longDescription);
      const level = messages.some(message => message.level === ValidationLevel.error) ? ValidationLevel.error : ValidationLevel.success;

      validation = level === ValidationLevel.success
        ? { ...state.validation, language: undefined }
        : { ...state.validation, language: { level, messages } };
    } else {
      validation = { ...state.validation, language: undefined };
    }

    return {
      ...state,
      validation
    };
  },

  [ACTION.RESET_DESCRIPTION_VALIDATION]: (state: CodetableItemState, action: ResetDescriptionValidation): CodetableItemState => {

    const source: ValidationProps = state.validation.language;

    let validation: { [key: string]: ValidationProps } = undefined;

    if (!!source && !!source.messages && source.messages.length > 0) {
      const messages = source.messages.filter(message => message.parameters.description !== action.languageId);
      const level = messages.some(message => message.level === ValidationLevel.error) ? ValidationLevel.error : ValidationLevel.success;

      validation = level === ValidationLevel.success
        ? { ...state.validation, language: undefined }
        : { ...state.validation, language: { level, messages } };
    } else {
      validation = { ...state.validation, language: undefined };
    }

    return {
      ...state,
      validation
    };
  },

  [ACTION.RESET_LONG_DESCRIPTION_VALIDATION]: (state: CodetableItemState, action: ResetLongDescriptionValidation): CodetableItemState => {

    const source: ValidationProps = state.validation.language;

    let validation: { [key: string]: ValidationProps } = undefined;

    if (!!source && !!source.messages && source.messages.length > 0) {
      const messages = source.messages.filter(message => message.parameters.longDescription !== action.languageId);
      const level = messages.some(message => message.level === ValidationLevel.error) ? ValidationLevel.error : ValidationLevel.success;

      validation = level === ValidationLevel.success
        ? { ...state.validation, language: undefined }
        : { ...state.validation, language: { level, messages } };
    } else {
      validation = { ...state.validation, language: undefined };
    }

    return {
      ...state,
      validation
    };
  },

  [ACTION.UPDATE_DESCRIPTION]: (state: CodetableItemState, action: UpdateDescription): CodetableItemState => {

    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        description: action.description
      }
    };
  },

  [ACTION.UPDATE_LONG_DESCRIPTION]: (state: CodetableItemState, action: UpdateLongDescription): CodetableItemState => {

    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        longDescription: action.longDescription
      }
    };
  },

  [ACTION.SWITCH_TRANSLATIONS_TOGGLE]: (state: CodetableItemState, action: SwitchTranslationsToggle): CodetableItemState => {

    return {
      ...state,
      hasTranslationsToggled: action.toggled === undefined ? !state.hasTranslationsToggled : action.toggled
    };
  },

  [ACTION.CLEAR_CODETABLE_OPTIONS]: (state: CodetableItemState, action: ClearCodetableOptions): CodetableItemState => {

    return {
      ...state,
      codetableOptions: {}
    };
  },

  [ACTION.UPDATE_BY_KEY]: (state: CodetableItemState, action: UpdateByKey): CodetableItemState => {
    return {
      ...state,
      codeItem: {
        ...state.codeItem,
        [action.key]: action.value
      }
    };
  },

  [ACTION.UPDATE_ORIGINAL_CODE_BY_KEY]: (state: CodetableItemState, action: UpdateOriginalCodeByKey): CodetableItemState => {

    return {
      ...state,
      originalCodeItem: {
        ...state.originalCodeItem,
        [action.key]: action.value
      }
    };
  },

  [ACTION.REQUEST_PARENT_CATEGORY]: (state: CodetableItemState, action: RequestParentCategory): CodetableItemState => {

    return {
      ...state,
      fetchingParentCategory: true
    };
  },

  [ACTION.RECEIVE_PARENT_CATEGORY]: (state: CodetableItemState, action: ReceiveParentCategory): CodetableItemState => {

    return {
      ...state,
      fetchingParentCategory: false
    };
  },

  [ACTION.FAIL_PARENT_CATEGORY]: (state: CodetableItemState, action: FailParentCategory): CodetableItemState => {

    return {
      ...state,
      fetchingParentCategory: false
    };
  },

  [ACTION.REQUEST_CHECK_IS_UOM_EXCLUDED]: (state: CodetableItemState, action: RequestCheckIsUomExcluded): CodetableItemState => {

    return {
      ...state,
      fetchingIsUomExcludedCheck: true
    };
  },

  [ACTION.RECEIVE_CHECK_IS_UOM_EXCLUDED]: (state: CodetableItemState, action: ReceiveCheckIsUomExcluded): CodetableItemState => {

    return {
      ...state,
      fetchingIsUomExcludedCheck: false,
      isUomExcluded: action.isUomExcluded
    };
  },

  [ACTION.FAIL_CHECK_IS_UOM_EXCLUDED]: (state: CodetableItemState, action: FailCheckIsUomExcluded): CodetableItemState => {

    return {
      ...state,
      fetchingIsUomExcludedCheck: false
    };
  },

  [ACTION.REQUEST_UOM_CONVERSION_FACTORS]: (state: CodetableItemState, action: RequestUomConversionFactors): CodetableItemState => {

    return {
      ...state,
      fetchingUomConversionFactors: true
    };
  },

  [ACTION.RECEIVE_UOM_CONVERSION_FACTORS]: (state: CodetableItemState, action: ReceiveUomConversionFactors): CodetableItemState => {

    return {
      ...state,
      fetchingUomConversionFactors: false,
      uomConversionFactors: action.data
    };
  },

  [ACTION.FAIL_UOM_CONVERSION_FACTORS]: (state: CodetableItemState, action: FailUomConversionFactors): CodetableItemState => {

    return {
      ...state,
      fetchingUomConversionFactors: false
    };
  },

  [ACTION.CREATE_INITIAL_UOM_CONVERSION_FACTORS]:
    (state: CodetableItemState, action: CreateInitialUomConversionFactors): CodetableItemState => {

      return {
        ...state,
        initialUomConversionFactors: action.uomConversionFactors
      };
    },

  [ACTION.CLEAR_UOM_CONVERSION_FACTORS]: (state: CodetableItemState, action: ClearUomConversionFactors): CodetableItemState => {

    return {
      ...state,
      uomConversionFactors: undefined
    };
  },

  [ACTION.CLEAR_UOM_EXCLUDED]: (state: CodetableItemState, action: ClearUomExcluded): CodetableItemState => {

    return {
      ...state,
      isUomExcluded: undefined
    };
  }
};

export const itemReducer: Reducer<CodetableItemState> =
  (state: CodetableItemState = INITIAL_CODETABLE_ITEM_STATE, action: any) => {
    const r = itemReducers[action.type];
    return (r && r(state, action)) || state;
  };
